'use client';
import { CaptchaRequestMessage, isCaptchaValidationError } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { GdprTextNewsletter } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-newsletter';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { LuMail } from 'react-icons/lu';

export function Newsletter() {
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
    onError: (captchaRequestError) => { if (isCaptchaValidationError(captchaRequestError)) { setIsAlreadySubscribed(false); return; } setIsAlreadySubscribed(true); },
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('p-2 sm:p-12', 'ea-homepage-newsletter')}>
      <form
        className={cn('grid grid-cols-12 gap-6 py-2', 'ea-homepage-newsletter__form')}
        onSubmit={handleSubmit}
      >
<CaptchaRequestMessage error={subscribeMutation.error} />
        <div
          className={cn(
            'col-span-12 flex flex-row items-center justify-start space-x-4 md:col-span-7 md:pr-7',
            'ea-homepage-newsletter__form-icon-email',
          )}
        >
          <div className={cn('rounded-full bg-accent p-4')}>
            <LuMail
              className={cn(
                'h-10 w-10 stroke-primary',
                'ea-homepage-newsletter__form-icon-email-icon',
              )}
            />
          </div>
          <div className={cn('flex flex-col gap-2', 'ea-homepage-newsletter__form-title')}>
            <h2
              data-ea-visual-tag="h4"
              className={cn(
                'max-w-sm text-2xl font-bold',
                'ea-homepage-newsletter__form-title-main',
              )}
            >
              {t('text610')}
            </h2>
            <p
              className={cn(
                'text-sm text-muted-foreground',
                'ea-homepage-newsletter__form-title-sub',
              )}
            >
              {t('text609')}
            </p>
          </div>
        </div>
        <div
          className={cn(
            'col-span-12 flex flex-col justify-center space-y-3 md:col-span-5',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <div className={cn('flex h-16 flex-row items-center sm:h-20')}>
            <Input
              aria-label={systemLabels('email')}
              id="newsletter-email"
              type="email"
              name="email"
              placeholder={t('text183')}
              onChange={(e) => setEmail(e.target.value)}
              className={{
                wrapper: cn('h-16 bg-white text-muted-foreground sm:h-20'),
                main: cn('h-16 w-full rounded-none border border-[#D4D4D4] bg-white pr-2 sm:h-20'),
              }}
              after={
                <Button
                  type="submit"
                  className={cn(
                    'h-14 rounded-none border-none bg-primary px-6 font-medium uppercase text-white sm:mr-1 sm:h-16 sm:px-12',
                  )}
                  disabled={email.length === 0 || !checked || isSubscribed}
                  isLoading={subscribeMutation.isPending}
                >
                  {t('text182')}
                </Button>
              }
            />
          </div>
          {isSubscribed && !isAlreadySubscribed && (
            <span className={cn('font-semibold text-success')}>{t('text3139')}</span>
          )}
          {isAlreadySubscribed && (
            <span className={cn('text-error font-semibold')}>{t('text234')}</span>
          )}
          <div
            className={cn(
              'flex flex-row items-start space-x-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id="newsletter-gdpr"
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor="newsletter-gdpr"
              className={cn('pt-[3px]', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
            >
              <GdprTextNewsletter locale={locale} />
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
